import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
// Helpers
import { errorLogger, getError } from "@/Common/helpers/logger";
import { getCellClassNameFn, isFooterCell } from "@/Common/helpers/Tables";
// Components
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
// Materials
import { Collapse, Divider, Grid } from "@mui/material";
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
// Types
import { COLUMN_RESIZE_FACTOR, DEFAULT_COLLAPSED_SIZE, HEADER_CLASS_NAME, ID_GRID_COLDEF } from "@/Washouts/constants";
import { GridStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { SalesTableProps } from "@/Washouts/types/finance";

// External
import isEqual from "lodash-es/isEqual";
import { DATE_FORMAT } from "@/Constants/date";
import { getFormattedDate } from "@dealerwave/dw-layer-date-converter";
import { useLocation } from "@Common/hooks/Location/useLocation.tsx";

let WIDE_COLUMN_WIDTH = 150;

function getColumns(includeEmployeeNameColumn: boolean, timezone: string): GridColDef[] {
	let columns: GridColDef[] = [ID_GRID_COLDEF];
	if (includeEmployeeNameColumn) {
		WIDE_COLUMN_WIDTH = 100;
		const DATA_PAGE_SCALAR_WIDTH = 1.5;
		columns = [
			...columns,
			{
				field: "employeeName",
				headerName: "Employee Name",
				width: WIDE_COLUMN_WIDTH * DATA_PAGE_SCALAR_WIDTH,
				headerClassName: HEADER_CLASS_NAME,
				cellClassName: getCellClassNameFn([], {}, {}),
				renderCell: (params) => {
					if (isFooterCell(params)) {
						return <TypographyCell hints={{ isFooter: true, noWrap: true, noFocus: true }}>Total</TypographyCell>;
					}
					return <TypographyCell params={params} />;
				},
				renderHeader: (params) => <TypographyHeader params={params} />,
			},
		];
	}
	return [
		...columns,
		{
			field: "dlcd",
			headerName: "DLCD",
			width: 75,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (params) => {
				if (isFooterCell(params) && !includeEmployeeNameColumn) {
					return <TypographyCell hints={{ isFooter: true, noWrap: true, noFocus: true }}>Total</TypographyCell>;
				}
				return <TypographyCell params={params} />;
			},
			valueFormatter: (value) => {
				return value;
			},
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "cancellationDate",
			headerName: "Eff. Cancel Date",
			width: 100,
			type: "date",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			valueGetter: (cancelDateValue: string) => cancelDateValue && new Date(cancelDateValue),
			valueFormatter: (cancelDateValue: string) => getFormattedDate(cancelDateValue, timezone, DATE_FORMAT.monthAbbrDatePadded),
			renderCell: (params) => <TypographyCell params={params} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "saleDate",
			headerName: "Eff. Sale Date",
			width: 100,
			type: "date",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			valueGetter: (saleDateValue: string) => saleDateValue && new Date(saleDateValue),
			valueFormatter: (saleDateValue: string) => getFormattedDate(saleDateValue, timezone, DATE_FORMAT.monthAbbrDatePadded),
			renderCell: (params) => <TypographyCell params={params} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "product",
			headerName: "Product",
			width: WIDE_COLUMN_WIDTH,
			minWidth: WIDE_COLUMN_WIDTH,
			maxWidth: WIDE_COLUMN_WIDTH * COLUMN_RESIZE_FACTOR,
			flex: 1,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (params) => <TypographyCell params={params} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "trntype",
			headerName: "TrnType",
			width: 80,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (params) => <TypographyCell params={params} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "amtDueWtyCo",
			headerName: "Amt Due Wty Co",
			type: "number",
			width: 110,
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], { isMoney: true }, {}),
			renderCell: (params) => <TypographyCell render="absolute" params={params} hints={{ isMoney: true }} />,
			renderHeader: (params) => <TypographyHeader params={params} left />,
		},
		{
			field: "lastName",
			headerName: "Last Name",
			width: WIDE_COLUMN_WIDTH,
			minWidth: WIDE_COLUMN_WIDTH,
			maxWidth: WIDE_COLUMN_WIDTH * COLUMN_RESIZE_FACTOR,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (params) => <TypographyCell params={params} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "firstName",
			headerName: "First Name",
			width: WIDE_COLUMN_WIDTH,
			minWidth: WIDE_COLUMN_WIDTH,
			maxWidth: WIDE_COLUMN_WIDTH * COLUMN_RESIZE_FACTOR,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (params) => <TypographyCell params={params} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "cancelAmount",
			headerName: "Cancel Amount",
			type: "number",
			width: 120,
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], { isMoney: true }, {}),
			renderCell: (params) => <TypographyCell render="absolute" params={params} hints={{ isMoney: true }} />,
			renderHeader: (params) => <TypographyHeader params={params} left />,
		},
		{
			field: "stockNumber",
			headerName: "Stock #",
			width: 100,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (params) => <TypographyCell params={params} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "dealNumber",
			headerName: "Deal #",
			width: 80,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (params) => <TypographyCell params={params} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "chargeBackGross",
			headerName: "C. B. Gross",
			type: "number",
			width: 120,
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn([], { isMoney: true }, {}),
			renderCell: (params) => <TypographyCell render="absolute" params={params} hints={{ isMoney: true }} />,
			renderHeader: (params) => <TypographyHeader params={params} left />,
		},
	];
}

function SalesTable(props: Readonly<SalesTableProps>) {
	const title = props.title ?? "Cancellations";
	const apiRef = useGridApiRef();

	const { timezone } = useLocation();

	// Collapsible attributes
	const [collapsed, setCollapsed] = useState(true);

	const columns = getColumns(props.includeEmployeeNameColumn ?? false, timezone);

	const clearFilters = useCallback(() => {
		// Clear filter on purpose
		try {
			const filter = apiRef.current?.state?.filter;
			if (filter) {
				const cleanFilter = {
					filterModel: { items: [], linkOperator: "and" },
					visibleRows: null,
					visibleRowsLookup: {},
				};

				apiRef.current.setState((prevState) => {
					return {
						...prevState,
						filter: { ...filter, ...cleanFilter },
					} as GridStatePremium;
				});
			}
		} catch (error) {
			errorLogger(getError(error));
		}
	}, [apiRef]);

	useEffect(() => {
		if (!props.notCollapsible) {
			clearFilters();
		}

		if (typeof props.collapsed !== "undefined") {
			setCollapsed(props.collapsed);
		}

		return () => {
			setCollapsed(true);
		};
	}, [props, apiRef, clearFilters]);

	const collapsibleToolbarFilterProps = {
		titleExpandible: {
			title,
			collapsed,
			hide: false,
			collapsible: true,
			titleClasses: "page-title",
			sxContainer: { padding: "20px 32px" },
			onClick: (value: boolean) => setCollapsed(value),
		},
		filter: {
			hide: false,
		},
		search: {
			data: props.data ?? [],
			hide: false,
			disabled: false,
		},
	};
	const notCollapsibleToolbarFilterProps = {
		titleExpandible: {
			className: "my-[14px] [&_.toolbar-actions]:!right-[50px] [&_.toolbar-actions]:!bottom-[-1]",
			hide: true,
		},
		filter: {
			hide: false,
		},
		search: {
			useQuickFilter: true,
			initiallyOpened: Boolean(props.filterModel?.quickFilterValues?.length),
		},
	};

	const dataTable = (toolbarProps: object, dataPageStyles: boolean) => (
		<DataSmartTable
			{...props.tableProps}
			sx={{ width: "1400px" }}
			apiRef={apiRef}
			columns={columns}
			rows={props.data ?? []}
			hideFooter
			disableColumnMenu
			disableRowSelectionOnClick
			disableMultipleRowSelection
			checkboxSelection={false}
			columnVisibilityModel={{
				_id: false,
			}}
			initialState={{
				aggregation: {
					model: {
						amtDueWtyCo: "sum",
						cancelAmount: "sum",
						chargeBackGross: "sum",
					},
				},
				filter: {
					filterModel: props.filterModel,
				},
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: toolbarProps,
				noRowsOverlay: {
					title: `No ${title} Data`,
				},
			}}
			getCellClassName={(params) => {
				// Targeting footer
				if (isFooterCell(params)) {
					// Use this to change the cell class name depending on the cell value
					const negatives = ["amtDueWtyCo", "cancelAmount", "chargeBackGross"];
					if (negatives.includes(params.field) && typeof params.value === "number" && params.value < 0) {
						return "aggregation-footer-negative";
					}

					// Append label 'Total' to the cell value in footer
					if (
						(!props.includeEmployeeNameColumn && params.field === "dlcd") ||
						(props.includeEmployeeNameColumn && params.field === "employeeName")
					) {
						return "aggregation-footer-total";
					}
				}
				return "";
			}}
			customProps={{
				tableClassName: {
					"[&_.MuiDataGrid-main]:mt-0 [&_.MuiDataGrid-main]:mb-[15px] ": true,
					"[&_.toolbar-actions]:absolute [&_.toolbar-actions]:top-[15px]": true,
					"[&_.MuiDataGrid-main]:mx-[30px]": !dataPageStyles,
					"[&_.MuiDataGrid-main]:ml-[0px] [&_.MuiDataGrid-main]:mr-[65px]": dataPageStyles,
				},
			}}
			onFilterModelChange={(newFilterModel) => {
				if (!isEqual(newFilterModel, props.filterModel)) {
					props.setFilterModel?.(newFilterModel);
				}
			}}
		/>
	);
	if (props.notCollapsible) {
		return dataTable(notCollapsibleToolbarFilterProps, true);
	}
	return (
		<>
			<Collapse in={!collapsed} collapsedSize={DEFAULT_COLLAPSED_SIZE} timeout={100}>
				{dataTable(collapsibleToolbarFilterProps, false)}
			</Collapse>

			<Collapse in={props.divider} collapsedSize={0} timeout={100}>
				{props.sxSectionDivider ? (
					<Divider sx={props.sxSectionDivider}></Divider>
				) : (
					<Divider
						className={clsx({
							"!mx-[24px] !border-b !border-[#CCC]": true,
							"pt-0": collapsed,
							"pt-[5px] !mt-[10px]": !collapsed,
						})}
					></Divider>
				)}
			</Collapse>
		</>
	);
}

export default function CancellationTable(props: Readonly<SalesTableProps>) {
	if (props.loading) {
		return (
			<>
				<Grid className="py-[20px] px-[32px]">
					<TableLoader smallTitle toolbar />
				</Grid>
				{props.divider &&
					(props.sxSectionDivider ? (
						<Divider sx={props.sxSectionDivider ?? {}}></Divider>
					) : (
						<Divider className="!mx-[24px] !border-b !border-[#CCC]"></Divider>
					))}
			</>
		);
	}

	return <>{props.dataPresent && <SalesTable {...props} />}</>;
}
